import { RecordStatus } from "../enums/RecordStatus";
import baseService from "./base.service";

export const getAllRecordsByDepartmentIdAndStatus = async (
  id: number,
  status: Array<RecordStatus>,
  pageNumber: number,
  pageSize: number,
  search?: string,
) => {
  const trimmed = search?.trim();
  return baseService
    .post(`/record/department?pageNumber=${pageNumber}&pageSize=${pageSize}`, {
      departmentId: id,
      status: status,
      search: trimmed || undefined,
    })
    .then((response) => response)
    .catch((error) => {
      console.log("api error:", error);
      throw error;
    });
};

export const getRecordById = async (id: number) => {
  return baseService
    .get(`/record/detail?id=${id}`)
    .then((response) => response)
    .catch((error) => {
      console.log("api error:", error);
      throw error;
    });
};

export type RecordAccessContext = {
  record_id: number;
  record_name: string;
  owner_display_name?: string | null;
  owner_email?: string | null;
  access_state: "granted" | "none" | "pending" | "rejected";
  can_request: boolean;
  request_id?: number | null;
  is_owner: boolean;
};

export type RecordAccessRequestItem = {
  id: number;
  record_id: number;
  requester_email: string;
  requester_name?: string | null;
  status: "Pending" | "Approved" | "Rejected";
  message?: string | null;
  created_at: string;
  resolved_at?: string | null;
};

export const normalizeAccessRequestStatus = (
  status: unknown,
): RecordAccessRequestItem["status"] => {
  if (status === "Pending" || status === 0 || status === "0") return "Pending";
  if (status === "Approved" || status === 1 || status === "1") return "Approved";
  if (status === "Rejected" || status === 2 || status === "2") return "Rejected";
  return "Pending";
};

export const normalizeAccessRequestItem = (item: any): RecordAccessRequestItem => ({
  id: item.id,
  record_id: item.record_id,
  requester_email: item.requester_email,
  requester_name: item.requester_name ?? null,
  status: normalizeAccessRequestStatus(item.status),
  message: item.message ?? null,
  created_at: item.created_at,
  resolved_at: item.resolved_at ?? null,
});

export const getAccessContext = async (recordId: number, inviteToken: string) => {
  return baseService.get("/record/access-context", {
    params: { record_id: recordId, invite_token: inviteToken },
  });
};

export const submitAccessRequest = async (
  recordId: number,
  inviteToken: string,
  message?: string,
) => {
  return baseService.post("/record/access-request", {
    record_id: recordId,
    invite_token: inviteToken,
    message: message ?? null,
  });
};

export const listAccessRequests = async (recordId: number) => {
  return baseService.get("/record/access-requests", {
    params: { record_id: recordId },
  });
};

export const approveAccessRequest = async (requestId: number) => {
  return baseService.post(`/record/access-request/${requestId}/approve`);
};

export const rejectAccessRequest = async (requestId: number) => {
  return baseService.post(`/record/access-request/${requestId}/reject`);
};

export type RecordAccessInviteStatus = {
  record_id: number;
  revoked: boolean;
  revoked_at?: string | null;
  expires_at?: string | null;
  has_active_invite: boolean;
};

export type BulkAccessRequestResult = {
  processed_count: number;
  request_ids: number[];
};

export type RecordAccessAuditEntry = {
  request_id: number;
  record_id: number;
  record_name: string;
  requester_email: string;
  requester_name?: string | null;
  status: "Pending" | "Approved" | "Rejected";
  message?: string | null;
  created_at: string;
  resolved_at?: string | null;
  resolved_by_name?: string | null;
  resolved_by_email?: string | null;
};

export type RecordAccessAuditSummary = {
  record_id: number;
  record_name: string;
  shared_count: number;
  pending_count: number;
  last_activity_at: string;
};

export const bulkApproveAccessRequests = async (recordId: number, requestIds?: number[]) => {
  return baseService.post("/record/access-requests/bulk-approve", {
    record_id: recordId,
    request_ids: requestIds ?? null,
  });
};

export const bulkRejectAccessRequests = async (recordId: number, requestIds?: number[]) => {
  return baseService.post("/record/access-requests/bulk-reject", {
    record_id: recordId,
    request_ids: requestIds ?? null,
  });
};

export const getAccessInviteStatus = async (recordId: number) => {
  return baseService.get("/record/access-invite-status", {
    params: { record_id: recordId },
  });
};

export const revokeAccessInvite = async (recordId: number) => {
  return baseService.post(`/record/revoke-access-invite?record_id=${recordId}`);
};

export const reEnableAccessInvite = async (recordId: number) => {
  return baseService.post(`/record/re-enable-access-invite?record_id=${recordId}`);
};

export const getAccessAudit = async (params: {
  pageNumber: number;
  pageSize: number;
  status?: string;
  since?: string;
  until?: string;
}) => {
  const query = new URLSearchParams({
    pageNumber: String(params.pageNumber),
    pageSize: String(params.pageSize),
  });
  if (params.status) query.set("status", params.status);
  if (params.since) query.set("since", params.since);
  if (params.until) query.set("until", params.until);
  return baseService.get(`/record/access-audit?${query.toString()}`);
};

export const getAccessAuditSummary = async (params: {
  pageNumber: number;
  pageSize: number;
  pendingOnly?: boolean;
  search?: string;
}) => {
  const query = new URLSearchParams({
    pageNumber: String(params.pageNumber),
    pageSize: String(params.pageSize),
  });
  if (params.pendingOnly) query.set("pendingOnly", "true");
  if (params.search?.trim()) query.set("search", params.search.trim());
  return baseService.get(`/record/access-audit/summary?${query.toString()}`);
};

export const retryMeetingSummary = async (recordId: number) => {
  return baseService
    .post(`/record/retry_meeting_summary?id=${recordId}`)
    .then((response) => response)
    .catch((error) => {
      console.log("api error:", error);
      throw error;
    });
};

export const retryJob = async (recordId: number) => {
  return baseService
    .post(`/record/retry_job?id=${recordId}`)
    .then((response) => response)
    .catch((error) => {
      console.log("api error:", error);
      throw error;
    });
};

export const removeSecretRecord = async (jobId: string | number) => {
  return baseService
    .get(`/record/remove_secret_file?id=${encodeURIComponent(String(jobId))}`)
    .then((response) => response)
    .catch((error) => {
      console.log("api error:", error);
    });
};

export const getUsageStatistics = async (since: string, until: string, departmentId: string) => {
  return baseService
    .get(`/record/usage_status?since=${since}&until=${until}&departmentId=${departmentId}`)
    .then((response) => response)
    .catch((error) => {
      console.log("api error:", error);
    });
};

// ── Speaker Assignments ──────────────────────────────────────────────────────

export type SpeakerAssignmentItem = {
  speaker_label: string;
  contact_id?: number | null;
  contact_name?: string | null;
  contact_email?: string | null;
  custom_name?: string | null;
};

export type RecordSpeakerAssignments = {
  can_edit?: boolean;
  speakers: SpeakerAssignmentItem[];
};

export type SaveSpeakerAssignment = {
  speaker_label: string;
  contact_id?: number | null;
  custom_name?: string | null;
};

export type SaveSpeakerAssignmentsRequest = {
  speakers: SaveSpeakerAssignment[];
};

export const getSpeakerAssignments = async (
  recordId: number,
): Promise<RecordSpeakerAssignments> => {
  const response = await baseService.get(`/record/${recordId}/speakers`);
  return response.data;
};

export const saveSpeakerAssignments = async (
  recordId: number,
  data: SaveSpeakerAssignmentsRequest,
): Promise<void> => {
  await baseService.put(`/record/${recordId}/speakers`, data);
};